import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { uploadImage } from './storageService';

const TEMPLATES_COLLECTION = 'templates';

/**
 * Upload template image (backward compatibility wrapper)
 * @deprecated Use uploadImage from storageService instead
 */
export const uploadTemplateImage = uploadImage;

/**
 * Backward compatibility aliases for old API
 * @deprecated Use loadTemplate, saveTemplate, deleteTemplate instead
 */
export const getTemplate = loadTemplate;
export const createTemplate = (userId, templateData) => saveTemplate(userId, null, templateData);
export const updateTemplate = async (templateId, templateData) => {
  // If userId not in templateData, fetch it from existing template
  let userId = templateData.userId;

  if (!userId) {
    const existingTemplate = await loadTemplate(templateId);
    userId = existingTemplate.userId;
  }

  return saveTemplate(userId, templateId, templateData);
};

/**
 * Save or update template in Firestore
 * @param {string} userId - User ID
 * @param {string} templateId - Template ID (null for new template)
 * @param {Object} templateData - Template data {name, canvasSize, layers, placeholders}
 * @returns {Promise<string>} - Template ID
 */
export async function saveTemplate(userId, templateId, templateData) {
  if (!userId) throw new Error('User ID is required');
  if (!templateData) throw new Error('Template data is required');

  const isNewTemplate = !templateId;
  const docId = templateId || doc(collection(db, TEMPLATES_COLLECTION)).id;

  const docRef = doc(db, TEMPLATES_COLLECTION, docId);

  const dataToSave = {
    ...templateData,
    userId,
    updatedAt: serverTimestamp(),
    ...(isNewTemplate && { createdAt: serverTimestamp() })
  };

  if (isNewTemplate) {
    await setDoc(docRef, dataToSave);
  } else {
    await updateDoc(docRef, dataToSave);
  }

  return docId;
}

/**
 * Load template from Firestore
 * @param {string} templateId - Template ID
 * @param {string} userId - User ID (for ownership verification)
 * @returns {Promise<Object>} - Template data with id
 */
export async function loadTemplate(templateId, userId) {
  if (!templateId) throw new Error('Template ID is required');

  const docRef = doc(db, TEMPLATES_COLLECTION, templateId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Template not found');
  }

  const data = docSnap.data();

  // Verify ownership
  if (userId && data.userId !== userId) {
    throw new Error('You do not have permission to access this template');
  }

  return {
    id: docSnap.id,
    ...data
  };
}

/**
 * Delete template from Firestore
 * @param {string} templateId - Template ID
 * @param {string} userId - User ID (for ownership verification)
 */
export async function deleteTemplate(templateId, userId) {
  if (!templateId) throw new Error('Template ID is required');

  // Verify ownership by loading the template (will throw if no permission)
  await loadTemplate(templateId, userId);

  const docRef = doc(db, TEMPLATES_COLLECTION, templateId);
  await deleteDoc(docRef);
}

/**
 * Get all templates for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of templates
 */
export async function getUserTemplates(userId, options = {}) {
  if (!userId) throw new Error('User ID is required');

  const constraints = [
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc')
  ];

  if (options.workspaceId) {
    constraints.push(where('workspaceId', '==', options.workspaceId));
  }

  const q = query(
    collection(db, TEMPLATES_COLLECTION),
    ...constraints
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Duplicate template
 * @param {string} templateId - Original template ID
 * @param {string} userId - User ID
 * @returns {Promise<string>} - New template ID
 */
export async function duplicateTemplate(templateId, userId) {
  if (!templateId) throw new Error('Template ID is required');

  const originalTemplate = await loadTemplate(templateId, userId);

  const newTemplateData = {
    name: `${originalTemplate.name} (Copy)`,
    canvasSize: originalTemplate.canvasSize,
    layers: originalTemplate.layers,
    placeholders: originalTemplate.placeholders
  };

  return saveTemplate(userId, null, newTemplateData);
}
